import React, { useState, useEffect } from 'react';
import {
  ComposedChart, Area, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine, ReferenceArea, PieChart, Pie, Cell,
} from 'recharts';
import {
  Loader2, TrendingUp, Users, MessageSquare, Megaphone, AlertTriangle,
  ExternalLink, Percent, Store, Trophy,
} from 'lucide-react';
import apiClient from '../api/client';

/**
 * Campaign-scoped Steam signals.
 *
 * The game page answers "what happened to this game" against every creator we
 * ever recorded. This answers "did our campaign move it": the same Steam curve,
 * overlaid with THIS campaign's own creators, against a pre-campaign baseline,
 * with the things that would otherwise be mistaken for our effect (patches,
 * sales, Steam front-page features) named rather than buried.
 */

// Platform casing is a compliance matter, not a style one — never rely on CSS
// `capitalize`, which turns YouTube into "Youtube".
//
// Flat brand colours, one per platform.
//
//   Twitch     #9146FF  Twitch Purple — the brand colour outright
//   YouTube    #FF0000  YouTube Red   — likewise
//   TikTok     #25F4EE  TikTok Cyan. The wordmark itself is BLACK, with cyan and
//                       red (#FE2C55) offset layers behind it. Black is invisible
//                       on this chart and red would be indistinguishable from
//                       YouTube, so cyan is the one usable official value.
//   Instagram  #E4405F  Instagram has no flat brand colour — its mark is a
//                       purple-pink-orange gradient — so this is the standard
//                       single-colour reduction of it, the one Instagram's own
//                       one-colour contexts use.
//   X          #FFFFFF  The mark is black on light and white on dark. This is dark.
const PLATFORMS = [
  { key: 'twitch', label: 'Twitch', color: '#9146FF' },
  { key: 'youtube', label: 'YouTube', color: '#FF0000' },
  { key: 'tiktok', label: 'TikTok', color: '#25F4EE' },
  { key: 'instagram', label: 'Instagram', color: '#E4405F' },
  { key: 'twitter', label: 'X', color: '#FFFFFF' },
  // Kick #53FC18 (Kick Green). No Kick content exists yet, but it is being added
  // elsewhere in the codebase — listed now so it arrives labelled rather than
  // landing in the grey catch-all below.
  { key: 'kick', label: 'Kick', color: '#00E70A' },
  // Catch-all so a platform added later shows up as an unlabelled band rather
  // than being silently dropped from a chart that claims to show everything.
  { key: 'other', label: 'Other', color: '#6B7280' },
];

const RANGES = [
  { key: '7', label: '7 days', days: 7 },
  { key: '30', label: '30 days', days: 30 },
  { key: '60', label: '60 days', days: 60 },
  { key: '90', label: '90 days', days: 90 },
  { key: 'all', label: 'Full campaign', days: null },
  { key: 'custom', label: 'Custom', days: null },
];

// Past this many days a daily bar is thinner than its own gap, so days roll
// into weeks.
const BUCKET_ABOVE_DAYS = 70;

// Language slice colours. Fixed per language rather than assigned by position,
// so English is the same colour in both pies and the two can be read against
// each other at a glance.
const LANG_COLOURS = {
  en: '#fe2388', es: '#A970FF', ru: '#25F4EE', fr: '#FFB020',
  de: '#53FC18', pt: '#FF0000', zh: '#4EA8FF', ja: '#E4405F',
  ko: '#B9C0CC', it: '#8134AF', pl: '#00C2A8', tr: '#F58529',
};
const LANG_FALLBACK = '#6B7280';

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString());
const dayLabel = (d) =>
  new Date(`${d}T00:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

const CampaignSteamTab = ({ campaignId }) => {
  const [range, setRange] = useState('30');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Fetched separately: the game-wide half of this is a self-join across the
  // whole coverage table, and the rest of the tab should not wait on it.
  const [affinity, setAffinity] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient.get(`/campaigns/${campaignId}/steam`)
      .then((r) => { if (!cancelled) setData(r.data); })
      // A failed request is NOT the same as "this campaign has no Steam data",
      // and saying so would tell the user something false about their campaign.
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e.message || 'Request failed'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [campaignId]);

  // Window bounds, computed here rather than further down because the affinity
  // request needs them and hooks cannot run after an early return. Safe against
  // `data` being null on the first pass.
  const allSeries = data?.series || [];
  const rangeDays = (RANGES.find((r) => r.key === range) || {}).days;
  let windowed;
  if (range === 'custom' && customFrom && customTo) {
    windowed = allSeries.filter((d) => d.date >= customFrom && d.date <= customTo);
  } else if (rangeDays) {
    // From the most recent day back, not from the campaign start — the recent
    // end is the part anyone is looking at.
    windowed = allSeries.slice(-rangeDays);
  } else {
    windowed = allSeries;
  }
  const winFrom = windowed[0]?.date || null;
  const winTo = windowed[windowed.length - 1]?.date || null;

  useEffect(() => {
    if (!winFrom || !winTo) return undefined;
    let cancelled = false;
    // Not cleared while refetching: blanking the panels on every range change
    // makes the page jump. They just update when the new numbers land.
    apiClient.get(`/campaigns/${campaignId}/steam/affinity`, { params: { from: winFrom, to: winTo } })
      .then((r) => { if (!cancelled) setAffinity(r.data); })
      .catch(() => { if (!cancelled) setAffinity({ available: false, reason: 'error' }); });
    return () => { cancelled = true; };
  }, [campaignId, winFrom, winTo]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-white text-sm py-10 justify-center">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading Steam signals…
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[#241d33] border border-sc-pink rounded-xl p-4 flex gap-2.5">
        <AlertTriangle className="w-4 h-4 text-sc-pink flex-none mt-0.5" />
        <div className="text-sm text-white">
          <span className="font-semibold">Couldn't load Steam signals.</span>{' '}
          {error} — this is a loading problem, not a statement about the campaign's data.
        </div>
      </div>
    );
  }

  if (!data?.linked) {
    return (
      <div className="bg-[#241d33] border border-white rounded-xl p-5 text-sm text-white">
        {data?.reason === 'no-game'
          ? 'This campaign has no game attached, so there is nothing to read from Steam.'
          : <>
              <span className="font-semibold">{data?.game?.title || 'This game'}</span> isn't linked
              to a Steam app yet. Link it on the game's page and recording starts from that day —
              Steam serves only current values, so history cannot be backfilled.
            </>}
      </div>
    );
  }

  const {
    series, lift, share, news, featuring, sales, campaign, game, achievements,
    leaderboards,
  } = data;

  const startDay = campaign.startDate ? campaign.startDate.slice(0, 10) : null;
  const endDay = campaign.endDate ? campaign.endDate.slice(0, 10) : null;

  // ---- bucketing ------------------------------------------------------
  // Peaks take the week's MAX because a peak is already a maximum and averaging
  // it would print a number that never happened; volumes take the SUM.
  const bucketed = windowed.length > BUCKET_ABOVE_DAYS;
  const rows = bucketed ? (() => {
    const weeks = new Map();
    for (const d of windowed) {
      const dt = new Date(`${d.date}T00:00:00Z`);
      const dow = (dt.getUTCDay() + 6) % 7; // Monday-anchored
      const key = new Date(dt.getTime() - dow * 86400000).toISOString().slice(0, 10);
      if (!weeks.has(key)) {
        weeks.set(key, {
          date: key, peakCcu: null, campItems: 0, campWatchHours: 0,
          campViews: 0, campCreators: 0,
          ...Object.fromEntries(PLATFORMS.map((p) => [p.key, 0])),
        });
      }
      const w = weeks.get(key);
      if (d.peakCcu != null) w.peakCcu = Math.max(w.peakCcu ?? 0, d.peakCcu);
      for (const p of PLATFORMS) w[p.key] += d[p.key] || 0;
      w.campItems += d.campItems;
      w.campWatchHours += d.campWatchHours;
      w.campViews += d.campViews;
      w.campCreators = Math.max(w.campCreators, d.campCreators);
    }
    return [...weeks.values()].sort((a, b) => a.date.localeCompare(b.date));
  })() : windowed;

  const chart = rows.map((d) => ({
    ...d,
    label: bucketed ? `w/c ${dayLabel(d.date)}` : dayLabel(d.date),
  }));

  const firstDay = chart.length ? chart[0].date : null;
  const lastDay = chart.length ? chart[chart.length - 1].date : null;
  // Always drawn. Thin and semi-transparent, so twenty of them read as texture
  // behind the data rather than hatching over it — the earlier version hid them
  // past a threshold, which threw away real information to solve a styling
  // problem.
  const newsMarks = (news || []).map((n) => {
    const day = new Date(n.publishedAt).toISOString().slice(0, 10);
    if (!firstDay || day < firstDay || day > lastDay) return null;
    const row = bucketed
      ? [...chart].reverse().find((c) => c.date <= day)
      : chart.find((c) => c.date === day);
    return row ? { gid: n.gid, label: row.label } : null;
  }).filter(Boolean);

  const playerDays = chart.filter((c) => c.peakCcu != null).length;

  const campaignBand = (() => {
    if (!startDay || !chart.length) return null;
    // Shading the whole plot says nothing — it earns its place only when part of
    // the view sits outside the campaign to contrast against.
    const anyOutside = chart.some((c) => c.date < startDay || (endDay && c.date > endDay));
    if (!anyOutside) return null;
    const first = chart.find((c) => c.date >= startDay);
    const last = endDay
      ? [...chart].reverse().find((c) => c.date <= endDay)
      : chart[chart.length - 1];
    if (!first || !last || first.date > last.date) return null;
    return { x1: first.label, x2: last.label };
  })();

  // Judged over the WHOLE campaign, not the visible range. Instagram's most
  // recent post here was 30 Aug, so on a 7-day view it has nothing — and having
  // it silently drop out of the chart and the key reads as a bug rather than as
  // a quiet week. A platform the campaign used stays listed; its bars are simply
  // zero-height on days it did nothing.
  const activePlatforms = PLATFORMS.filter((p) => series.some((c) => c[p.key] > 0));

  // Counted over exactly the rows the chart is drawing, so this panel moves with
  // the range selector instead of quietly staying campaign-wide while the chart
  // above it shows seven days.
  const NO_DENOMINATOR = ['tiktok', 'instagram', 'twitter'];
  const noDenominatorTotals = PLATFORMS
    .filter((p) => NO_DENOMINATOR.includes(p.key))
    .map((p) => ({ ...p, count: chart.reduce((n, c) => n + (c[p.key] || 0), 0) }));
  const rangeNote = range === 'all'
    ? ' this campaign'
    : range === 'custom' && customFrom && customTo
      ? ` ${new Date(`${customFrom}T00:00:00Z`).toLocaleDateString()}–${new Date(`${customTo}T00:00:00Z`).toLocaleDateString()}`
      : ` in the last ${(RANGES.find((r) => r.key === range) || {}).label || ''}`;
  const sharePct = (o) => (o && o.total > 0 ? Math.round((o.campaign / o.total) * 100) : null);
  const twPct = sharePct(share.twitch);
  const ytPct = sharePct(share.youtube);

  return (
    <div className="space-y-4">
      {/* ---- headline tiles ---- */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-[#241d33] border border-white rounded-xl p-3">
          <div className="flex items-center gap-1.5 text-xs text-white mb-1">
            <TrendingUp className="w-3.5 h-3.5 text-sc-pink" /> Players During Campaign
          </div>
          <div className="text-2xl font-bold text-white tabular-nums">{fmt(lift.duringAvgPeak)}</div>
          <div className="text-xs text-white">
            {lift.duringAvgPeak != null
              ? `average daily peak · ${lift.duringDays} day${lift.duringDays === 1 ? '' : 's'} recorded`
              : 'no readings in this period yet'}
          </div>
        </div>

        <div className="bg-[#241d33] border border-white rounded-xl p-3">
          <div className="flex items-center gap-1.5 text-xs text-white mb-1">
            <Percent className="w-3.5 h-3.5 text-sc-pink" /> vs Before Campaign
          </div>
          {/* A lift figure off a thin baseline is worse than no figure — it is a
              number you would have to defend. Withheld, with the reason stated,
              until there is enough pre-campaign history to stand behind. */}
          {lift.baselineDays >= 3 && lift.changePct != null ? (
            <>
              <div className="text-2xl font-bold text-white tabular-nums">
                {lift.changePct >= 0 ? '+' : ''}{lift.changePct}%
              </div>
              <div className="text-xs text-white">
                {fmt(lift.baselineAvgPeak)} → {fmt(lift.duringAvgPeak)} avg peak
              </div>
            </>
          ) : (
            <>
              <div className="text-2xl font-bold text-white tabular-nums">—</div>
              <div className="text-xs text-white">
                {lift.baselineDays === 0
                  ? 'no recording before this campaign started'
                  : `only ${lift.baselineDays} day${lift.baselineDays === 1 ? '' : 's'} of baseline`}
              </div>
            </>
          )}
        </div>

        <div className="bg-[#241d33] border border-white rounded-xl p-3">
          <div className="flex items-center gap-1.5 text-xs text-white mb-1">
            <Users className="w-3.5 h-3.5 text-sc-pink" /> Share of Voice
          </div>
          {/* Measured only from the day we began watching the whole game on each
              platform. Counting our content against days with no game-wide
              recording produced "100%", which was a measurement gap dressed as a
              result. Each figure now carries the window it was measured over. */}
          <div className="text-2xl font-bold text-white tabular-nums">
            {twPct != null ? `${twPct}%` : '—'}
          </div>
          <div className="text-xs text-white">
            {share.twitch?.total > 0
              ? <>{fmt(share.twitch.campaign)} of {fmt(share.twitch.total)} Twitch streams</>
              : 'no Twitch coverage recorded'}
          </div>
          {ytPct != null && (
            <div className="text-xs text-white mt-1">
              <span className="text-sc-pink">{ytPct}%</span> of YouTube
              {' '}({fmt(share.youtube.campaign)} of {fmt(share.youtube.total)})
            </div>
          )}
        </div>

        <div className="bg-[#241d33] border border-white rounded-xl p-3">
          <div className="flex items-center gap-1.5 text-xs text-white mb-1">
            <MessageSquare className="w-3.5 h-3.5 text-sc-pink" /> Reviews During Campaign
          </div>
          <div className="text-2xl font-bold text-white tabular-nums">{fmt(lift.duringReviewsTotal)}</div>
          <div className="text-xs text-white">
            {lift.duringReviewsPerDay != null ? `${lift.duringReviewsPerDay}/day` : 'none recorded'}
            {lift.baselineDays >= 3 && lift.baselineReviewsPerDay != null &&
              <> · was {lift.baselineReviewsPerDay}/day before</>}
          </div>
        </div>
      </div>

      {/* ---- the overlay ---- */}
      <div className="bg-[#241d33] border border-white rounded-xl p-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
          <h4 className="text-sm font-semibold text-white">
            {game.title} | Daily Peak Players vs Campaign Creator Coverage
            {bucketed && <span className="text-white"> (weekly)</span>}
          </h4>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-0.5 bg-[#1a1526] border border-white/25 rounded-lg p-0.5">
              {RANGES.map((r) => (
                <button
                  key={r.key}
                  onClick={() => setRange(r.key)}
                  className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
                    range === r.key ? 'bg-sc-pink text-white' : 'text-white hover:text-sc-pink'
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <a
              href={`https://store.steampowered.com/app/${game.steamAppId}/`}
              target="_blank" rel="noopener noreferrer"
              className="text-xs text-white hover:text-sc-pink inline-flex items-center gap-1"
            >
              Steam page <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>

        {range === 'custom' && (
          <div className="flex flex-wrap items-center gap-2 mb-2 text-xs text-white">
            <label className="flex items-center gap-1.5">
              From
              <input
                type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                min={series[0]?.date} max={series[series.length - 1]?.date}
                className="bg-[#1a1526] border border-white/25 rounded-md px-2 py-1 text-white"
              />
            </label>
            <label className="flex items-center gap-1.5">
              to
              <input
                type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                min={series[0]?.date} max={series[series.length - 1]?.date}
                className="bg-[#1a1526] border border-white/25 rounded-md px-2 py-1 text-white"
              />
            </label>
            {(!customFrom || !customTo) && <span className="text-white">pick both dates</span>}
          </div>
        )}


        {chart.length < 2 ? (
          <div className="text-sm text-white py-8 text-center">
            {range === 'custom' && (!customFrom || !customTo)
              ? 'Pick a start and end date to draw the chart.'
              : 'Not enough recorded days in this range to draw a chart.'}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={340}>
            <ComposedChart data={chart} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="campSteamCcu" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#fe2388" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#fe2388" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.12)" />
              <XAxis dataKey="label" tick={{ fill: '#fff', fontSize: 11 }} stroke="rgba(255,255,255,0.25)" />
              <YAxis
                yAxisId="left" tick={{ fill: '#fff', fontSize: 11 }}
                stroke="rgba(255,255,255,0.25)" width={76}
                label={{ value: 'Player Count', angle: -90, position: 'insideLeft', fill: '#fff', fontSize: 11, offset: 6, style: { textAnchor: 'middle' } }}
              />
              {/* Content pieces, LINEAR and stacked. The previous version put
                  watch hours and views on logarithmic axes, where a value a
                  hundred times smaller still drew at two-thirds height — so every
                  bar looked alike and the bars carried almost no information.
                  Counting pieces gives one unit every platform shares, which is
                  what makes them stackable and honestly comparable. */}
              <YAxis
                yAxisId="items" orientation="right" allowDecimals={false}
                tick={{ fill: '#fff', fontSize: 11 }} stroke="rgba(255,255,255,0.25)" width={46}
                label={{ value: 'Posts', angle: 90, position: 'insideRight', fill: '#fff', fontSize: 11, offset: 6, style: { textAnchor: 'middle' } }}
              />
              <Tooltip
                contentStyle={{ background: '#241d33', border: '1px solid #fff', borderRadius: 10, color: '#fff' }}
                labelStyle={{ color: '#fff' }}
                formatter={(v, n) => [v == null ? '—' : Number(v).toLocaleString(), n]}
              />
              {campaignBand && (
                <ReferenceArea
                  yAxisId="left" x1={campaignBand.x1} x2={campaignBand.x2}
                  fill="#fe2388" fillOpacity={0.07} stroke="none"
                />
              )}
              {newsMarks.map((n) => (
                <ReferenceLine
                  key={n.gid} yAxisId="left" x={n.label}
                  stroke="#ffffff" strokeOpacity={0.75} strokeWidth={1.5} strokeDasharray="5 3"
                />
              ))}
              {activePlatforms.map((p) => (
                <Bar
                  key={p.key} yAxisId="items" dataKey={p.key} name={p.label}
                  stackId="posts" fill={p.color} fillOpacity={0.85}
                />
              ))}
              {/* Dark casing so the line stays legible over the bars */}
              <Area
                yAxisId="left" type="monotone" dataKey="peakCcu" legendType="none" tooltipType="none"
                stroke="#241d33" strokeWidth={7} fill="none" connectNulls={false} dot={false}
                activeDot={false} isAnimationActive={false}
              />
              <Area
                yAxisId="left" type="monotone" dataKey="peakCcu"
                name={bucketed ? 'Peak players (week high)' : 'Peak players'}
                stroke="#fe2388" strokeWidth={3} fill="url(#campSteamCcu)" connectNulls={false}
                dot={{ r: 3, fill: '#fe2388', stroke: '#241d33', strokeWidth: 1.5 }}
                activeDot={{ r: 6, fill: '#fe2388', stroke: '#fff', strokeWidth: 2 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}

        {chart.length >= 2 && (
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 mt-2">
            <span className="flex items-center gap-1.5 text-xs text-white">
              <svg width="18" height="8" aria-hidden="true">
                <line x1="0" y1="4" x2="18" y2="4" stroke="#fe2388" strokeWidth="2" strokeOpacity={playerDays >= 2 ? 1 : 0.4} />
                <circle cx="9" cy="4" r="2.5" fill="#fe2388" fillOpacity={playerDays >= 2 ? 1 : 0.4} />
              </svg>
              Peak Players
              {playerDays < 2 && (
                <span className="text-white">({playerDays === 0 ? 'no readings yet' : '1 reading so far'})</span>
              )}
            </span>
            {activePlatforms.map((p) => (
              <span key={p.key} className="flex items-center gap-1.5 text-xs text-white">
                <span className="inline-block w-3 h-3 rounded-sm" style={{ background: p.color }} />
                {p.label}
              </span>
            ))}
            {newsMarks.length > 0 && (
              <span className="flex items-center gap-1.5 text-xs text-white">
                <svg width="18" height="8" aria-hidden="true">
                  <line x1="9" y1="0" x2="9" y2="8" stroke="#fff" strokeOpacity="0.85" strokeWidth="1.5" strokeDasharray="5 3" />
                </svg>
                Developer update ({newsMarks.length})
              </span>
            )}
            {campaignBand && (
              <span className="flex items-center gap-1.5 text-xs text-white">
                <span className="inline-block w-3 h-3 rounded-sm" style={{ background: 'rgba(254,35,136,0.25)' }} />
                Campaign period
              </span>
            )}
          </div>
        )}
      </div>

      {/* ---- who is actually covering this game ---- */}
      {leaderboards && (leaderboards.youtube?.overall?.length > 0 || leaderboards.twitch?.overall?.length > 0) && (
        <div className="grid md:grid-cols-2 gap-3">
          {[
            { key: 'youtube', label: 'YouTube', board: leaderboards.youtube },
            { key: 'twitch', label: 'Twitch', board: leaderboards.twitch },
          ].filter((x) => x.board?.overall?.length > 0).map(({ key, label, board }) => {
            const colour = (PLATFORMS.find((p) => p.key === key) || {}).color;
            return (
              <div key={key} className="bg-[#241d33] border border-white rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-block w-3 h-3 rounded-sm" style={{ background: colour }} />
                  <h4 className="text-sm font-semibold text-white">{label} | Top Creators</h4>
                </div>
                <p className="text-xs text-white mb-3">
                  <span className="text-sc-pink font-semibold">{fmt(board.ours)}</span> of{' '}
                  {fmt(board.total)} creators covering {game.title} are on this campaign
                  and are ranked by {board.unit}.
                </p>

                {[
                  { title: 'Everyone', rows: board.overall, flag: true },
                  { title: 'This Campaign', rows: board.campaign, flag: false },
                ].map(({ title, rows: list, flag }) => (
                  <div key={title} className="mb-3 last:mb-0">
                    <div className="text-xs font-semibold text-white mb-1.5">{title}</div>
                    {list.length === 0 ? (
                      <div className="text-xs text-white">
                        None of this campaign's creators appear in the game-wide {label} scan.
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {list.map((r, i) => (
                          <div
                            key={r.id}
                            className={`flex items-center gap-2 text-xs rounded-md px-2 py-1 ${
                              flag && r.isOurs ? 'bg-sc-pink/20 border border-sc-pink/40' : ''
                            }`}
                          >
                            <span className="text-white tabular-nums w-4 flex-none">{i + 1}</span>
                            <span className="text-white truncate flex-1" title={r.name}>{r.name}</span>
                            {flag && r.isOurs && (
                              <span className="text-sc-pink font-semibold flex-none">ours</span>
                            )}
                            <span className="text-white tabular-nums flex-none">{fmt(r.metric)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* ---- what else these creators play ---- */}
      {affinity?.available && affinity.games?.length > 0 && (
        <div className="bg-[#241d33] border border-white rounded-xl p-4">
          <h4 className="text-sm font-semibold text-white mb-1">
            What Else These Creators Play
          </h4>
          {/* Twitch only, and said so: on Twitch the streamer sets the game
              category and it is authoritative, whereas YouTube attribution is
              matched on title keywords and short game names catch everything. */}
          <p className="text-xs text-white mb-3">
            The games this campaign's creators streamed most, aside from {game.title}.
            From Twitch, where the game category is set by the streamer.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {affinity.games.map((g) => (
              <div key={g.gameId} className="flex flex-col">
                <div className="aspect-[3/4] rounded-lg overflow-hidden bg-[#1a1526] border border-white/20 flex items-center justify-center">
                  {g.coverImageUrl ? (
                    <img
                      src={g.coverImageUrl} alt={g.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    // Not every game in the catalogue has cover art, so the tile
                    // has to stand on its own rather than leaving a broken frame.
                    <span className="text-xs text-white text-center px-2 leading-snug">{g.title}</span>
                  )}
                </div>
                <div className="text-xs font-semibold text-white mt-1.5 truncate" title={g.title}>
                  {g.title}
                </div>
                <div className="text-xs text-white">
                  {g.creators} creator{g.creators === 1 ? '' : 's'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---- language mix ---- */}
      {affinity?.available && affinity.languages?.length > 1 && (
        <div className="bg-[#241d33] border border-white rounded-xl p-4">
          <h4 className="text-sm font-semibold text-white mb-1">Language Mix</h4>
          {/* Language, NOT region. We hold a location for a couple of hundred of
              the several thousand creators covering the game, so a region
              comparison would be mostly guesswork — and inferring region from
              language is the exact mistake that once filed every UK Twitch
              creator under North America. */}
          <p className="text-xs text-white mb-3">
            Broadcast language of this Campaign's Creators vs Creators Outside of the Campaign.
          </p>

          <div className="flex flex-wrap items-center justify-around gap-4">
            {[
              { title: 'This Campaign', key: 'ourPct', total: affinity.languageCohorts.ours },
              { title: 'Outside the Campaign', key: 'allPct', total: affinity.languageCohorts.all },
            ].map(({ title, key, total }) => {
              const slices = affinity.languages
                .filter((l) => (l[key] || 0) > 0)
                .map((l) => ({ name: l.label, value: l[key], code: l.code }));
              return (
                <div key={title} className="flex flex-col items-center">
                  <div className="text-xs font-semibold text-white mb-1">{title}</div>
                  <PieChart width={150} height={150}>
                    <Pie
                      data={slices} dataKey="value" nameKey="name"
                      cx="50%" cy="50%" innerRadius={34} outerRadius={68}
                      stroke="#241d33" strokeWidth={2} isAnimationActive={false}
                    >
                      {slices.map((sl) => (
                        <Cell key={sl.code} fill={LANG_COLOURS[sl.code] || LANG_FALLBACK} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: '#241d33', border: '1px solid #fff', borderRadius: 10, color: '#fff' }}
                      formatter={(v, n) => [`${v}%`, n]}
                    />
                  </PieChart>
                  <div className="text-xs text-white mt-1">{fmt(total)} creators</div>
                </div>
              );
            })}
          </div>

          {/* One key for both pies — the colours are fixed per language, so a
              separate legend under each would just repeat itself. */}
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 mt-3">
            {affinity.languages.map((l) => (
              <span key={l.code} className="flex items-center gap-1.5 text-xs text-white">
                <span
                  className="inline-block w-2.5 h-2.5 rounded-sm"
                  style={{ background: LANG_COLOURS[l.code] || LANG_FALLBACK }}
                />
                {l.label} <span className="text-white">{l.ourPct}% / {l.allPct}%</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ---- platforms with no game-wide denominator ---- */}
      {noDenominatorTotals.some((p) => p.count > 0) && (
        <div className="bg-[#241d33] border border-white rounded-xl p-4">
          <h4 className="text-sm font-semibold text-white mb-1">
            Also published{rangeNote}
          </h4>
          {/* Said plainly: these carry no share-of-voice figure because neither
              platform publishes a per-game directory to measure a total against.
              An absent denominator is not a small one. */}
          <p className="text-xs text-white mb-3">
            Counts of what your creators posted, not a proportion of everyone who posted.
          </p>
          <div className="flex flex-wrap gap-4">
            {noDenominatorTotals.filter((p) => p.count > 0).map((p) => (
              <div key={p.key} className="flex items-center gap-2">
                <span className="inline-block w-3 h-3 rounded-sm" style={{ background: p.color }} />
                <span className="text-sm text-white">
                  <span className="font-bold tabular-nums">{fmt(p.count)}</span> {p.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---- confounds ---- */}
      {(sales?.length > 0 || featuring?.length > 0 || news?.length > 0) && (
        <div className="bg-[#241d33] border border-white rounded-xl p-4">
          <h4 className="text-sm font-semibold text-white mb-3">
            Announcements &amp; Updates Throughout the Campaign Period
          </h4>
          <div className="space-y-2">
            {sales?.length > 0 && (
              <div className="flex items-start gap-2 text-xs text-white">
                <Percent className="w-3.5 h-3.5 text-sc-pink flex-none mt-0.5" />
                <span>
                  <span className="font-semibold">On sale for {sales.length} day{sales.length === 1 ? '' : 's'}</span>
                  {' '}— up to {Math.max(...sales.map((s) => s.discountPercent))}% off
                  {' '}({dayLabel(sales[0].date)}–{dayLabel(sales[sales.length - 1].date)})
                </span>
              </div>
            )}
            {featuring?.map((f, i) => (
              <div key={i} className="flex items-start gap-2 text-xs text-white">
                <Store className="w-3.5 h-3.5 text-sc-pink flex-none mt-0.5" />
                <span>
                  <span className="font-semibold">Featured on the Steam store</span> — {f.sectionName || f.section}
                  {f.isCurrent ? ' (still there)' : ''}
                  {' '}from {new Date(f.firstSeenAt).toLocaleDateString()}
                </span>
              </div>
            ))}
            {news?.slice(0, 8).map((n) => (
              <div key={n.gid} className="flex items-start gap-2 text-xs text-white">
                <Megaphone className="w-3.5 h-3.5 text-sc-pink flex-none mt-0.5" />
                <span>
                  {new Date(n.publishedAt).toLocaleDateString()} —{' '}
                  <a href={n.url} target="_blank" rel="noopener noreferrer" className="hover:text-sc-pink underline">
                    {n.title}
                  </a>
                </span>
              </div>
            ))}
            {news?.length > 8 && (
              <div className="text-xs text-white">…and {news.length - 8} more updates in this period.</div>
            )}
          </div>
        </div>
      )}

      {/* ---- achievements ---- */}
      {achievements?.length > 0 && (
        <div className="bg-[#241d33] border border-white rounded-xl p-4">
          <h4 className="text-sm font-semibold text-white mb-1 flex items-center gap-1.5">
            <Trophy className="w-4 h-4 text-sc-pink" /> Player Progression
          </h4>
          {/* The one thing here that is about depth rather than volume: a
              campaign that brings players who quit in ten minutes looks identical
              on the player curve and quite different on these. */}
          <p className="text-xs text-white mb-3">
            The share of everyone who owns {game.title} that has unlocked each achievement.
          </p>
          <div className="space-y-2">
            {achievements.slice(0, 6).map((a) => (
              <div key={a.name} className="flex items-center gap-3">
                <div className="text-xs text-white w-44 flex-none truncate" title={a.name}>{a.name}</div>
                <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-sc-pink rounded-full" style={{ width: `${Math.min(100, a.percent)}%` }} />
                </div>
                <div className="text-xs text-white tabular-nums w-24 text-right flex-none">
                  {a.percent}%
                  {a.changePct != null && a.changePct !== 0 && (
                    <span className="text-sc-pink"> {a.changePct > 0 ? '+' : ''}{a.changePct}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-white">
        Steam data via the Steam Web API, shown as-is. Not endorsed by or affiliated with Valve.
        Player counts are concurrent players sampled through the day — Steam publishes no daily
        active user figure. Share of voice is measured only from the day game-wide tracking began
        on each platform, and the game-wide scan skips streams below its viewer floor. Player
        numbers move for reasons of their own; where a rise overlaps a patch, a sale or a store
        feature, it can't be claimed as the campaign's on this evidence.
      </p>
    </div>
  );
};

export default CampaignSteamTab;
